/**
 * Copyright (C) 2018-present, Botobag Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  ImageTag,
  ProductData,
} from '@/models';

import { id } from './id';

import FulfillmentService from './fulfillmentServices';

 /* tslint:disable:max-line-length */

const products: ProductData[] = [{
  id: id(),
  name: ' Asus 華碩 VivoBook Max X541NA - 15.6" HD - Pentium N4200 - 4GB - 500GB HDD',

  featureBullets: [
    '配備 Intel Pentium N4200 四核心處理器、15.6" HD LED 鏡面寬螢幕 (1366 x 768)',
    '4GB DDR 記憶體、500GB 硬碟',
    '配備 USB 3.1 Type-C 及藍芽 4.0',
    '超輕盈 1.8 kg',
    '隨機安裝 Windows 10 Home 作業系統',
  ],
  brand: 'Asus 華碩',
  priceCurrency: 'TWD',
  options: [
    {
      id: id(),
      name: '顏色',
    },
  ],
  variants: [
    {
      id: id(),
      options: ['黑色'],
      images: [
        { id: 50, tag: ImageTag.MainImage },
        { id: 51, tag: ImageTag.ProductAlt1Image },
        { id: 52, tag: ImageTag.ProductAlt2Image },
        { id: 53, tag: ImageTag.ProductAlt3Image },
        { id: 54, tag: ImageTag.ProductAlt4Image },
        { id: 55, tag: ImageTag.ProductAlt5Image },
        { id: 56, tag: ImageTag.ProductAlt6Image },
        { id: 57, tag: ImageTag.ProductAlt7Image },
        { id: 58, tag: ImageTag.ProductAlt8Image },
        { id: 59, tag: ImageTag.ProductAlt9Image },
      ].map(({ id: imageId, tag }) => ({
        id: id(),
        tag,
        handle: `https://picsum.photos/600/600/?image=${imageId}`,
      })),
      price: '13900',
      compareAtPrice: '15900',
      inventory: {
        id: id(),
        quantity: 4,
        metadata: {
          restockDate: new Date(),
        },
      },
      fulfillments: [
        FulfillmentService.SevenEleven,
        FulfillmentService.Ecan,
        FulfillmentService.InStorePickup,
      ],
    },
    {
      id: id(),
      options: ['銀色'],
      images: [
        { id: 60, tag: ImageTag.MainImage },
        { id: 61, tag: ImageTag.ProductAlt1Image },
        { id: 62, tag: ImageTag.ProductAlt2Image },
        { id: 63, tag: ImageTag.ProductAlt3Image },
        { id: 64, tag: ImageTag.ProductAlt4Image },
        { id: 65, tag: ImageTag.ProductAlt5Image },
        { id: 66, tag: ImageTag.ProductAlt6Image },
        { id: 67, tag: ImageTag.ProductAlt7Image },
        { id: 68, tag: ImageTag.ProductAlt8Image },
        { id: 69, tag: ImageTag.ProductAlt9Image },
      ].map(({ id: imageId, tag }) => ({
        id: id(),
        tag,
        handle: `https://picsum.photos/600/600/?image=${imageId}`,
      })),
      price: '13900',
      compareAtPrice: '15900',
      inventory: {
        id: id(),
        quantity: 10,
        metadata: {
          restockDate: new Date(),
        },
      },
      fulfillments: [
        FulfillmentService.SevenEleven,
        FulfillmentService.Ecan,
        FulfillmentService.InStorePickup,
      ],
    },
  ],
},

{
  id: id(),
  name: 'OZEN 真空抗氧化破壁食物調理機',
  featureBullets: [
    '以真空狀態下進行攪拌，保留蔬果最原味營養',
    '調理過程大幅降低與空氣接觸機會，食材不再變黃氧化',
    '高強度刀刃的六葉片，果皮，冰塊，冷凍食品都可以粉碎得非常細膩',
    '隨機附贈專用真空耐熱儲物杯，透過真空存放讓你隨時可以喝到最新鮮飲料',
  ],
  brand: '漢森 Hanssem',
  priceCurrency: 'TWD',
  options: [
    {
      id: id(),
      name: '顏色',
    },
  ],
  variants: [
    {
      id: id(),
      name: 'OZEN 真空抗氧化破壁食物調理機 (紅色)',
      options: ['紅色'],
      images: [
        {
          id: id(),
          tag: ImageTag.MainImage,
          handle: 'https://images-na.ssl-images-amazon.com/images/I/51dapMMjhTL._SL1001_.jpg',
        },
        {
          id: id(),
          tag: ImageTag.ProductAlt1Image,
          handle: 'https://images-na.ssl-images-amazon.com/images/I/61nzRjm3XIL._SL1001_.jpg',
        },
        {
          id: id(),
          tag: ImageTag.ProductAlt2Image,
          handle: 'https://image-cdn-flare.qdm.cloud/q597b0bed3df23/image/data/2018/08/02/4667025ba56b205ce44f775922acc67e.jpg',
        },
        {
          id: id(),
          tag: ImageTag.ProductAlt3Image,
          handle: 'https://image-cdn-flare.qdm.cloud/q597b0bed3df23/image/data/2018/08/02/f0c935840fd7dc76ba99770c8ac1004b.jpg',
        },
        {
          id: id(),
          tag: ImageTag.ProductAlt4Image,
          handle: 'https://image-cdn-flare.qdm.cloud/q597b0bed3df23/image/data/2018/08/02/fdadbc5088bb054bc39c0642143064f6.jpg',
        },
        {
          id: id(),
          tag: ImageTag.ProductAlt5Image,
          handle: 'https://image-cdn-flare.qdm.cloud/q597b0bed3df23/image/data/2018/08/02/f0c935840fd7dc76ba99770c8ac1004b.jpg',
        },
        {
          id: id(),
          tag: ImageTag.ProductAlt6Image,
          handle: 'https://i.imgur.com/aNLHrfE.jpg',
        },
      ],
      inventory: {
        id: id(),
        quantity: 19,
      },
      price: '17800',
      fulfillments: [
        FulfillmentService.SevenEleven,
        FulfillmentService.Ecan,
        FulfillmentService.InStorePickup,
      ],
    },

    {
      id: id(),
      name: 'OZEN 真空抗氧化破壁食物調理機 (銀色)',
      options: ['銀色'],
      images: [
        {
          id: id(),
          tag: ImageTag.MainImage,
          handle: 'https://images-na.ssl-images-amazon.com/images/I/614dXQpq2vL._SL1001_.jpg',
        },
        {
          id: id(),
          tag: ImageTag.ProductAlt1Image,
          handle: 'https://images-na.ssl-images-amazon.com/images/I/51jrIHoQxdL._SL1001_.jpg',
        },
        {
          id: id(),
          tag: ImageTag.ProductAlt2Image,
          handle: 'https://image-cdn-flare.qdm.cloud/q597b0bed3df23/image/data/2018/08/02/4667025ba56b205ce44f775922acc67e.jpg',
        },
        {
          id: id(),
          tag: ImageTag.ProductAlt3Image,
          handle: 'https://image-cdn-flare.qdm.cloud/q597b0bed3df23/image/data/2018/08/02/f0c935840fd7dc76ba99770c8ac1004b.jpg',
        },
        {
          id: id(),
          tag: ImageTag.ProductAlt4Image,
          handle: 'https://image-cdn-flare.qdm.cloud/q597b0bed3df23/image/data/2018/08/02/fdadbc5088bb054bc39c0642143064f6.jpg',
        },
        {
          id: id(),
          tag: ImageTag.ProductAlt5Image,
          handle: 'https://image-cdn-flare.qdm.cloud/q597b0bed3df23/image/data/2018/08/02/f0c935840fd7dc76ba99770c8ac1004b.jpg',
        },
        {
          id: id(),
          tag: ImageTag.ProductAlt6Image,
          handle: 'https://i.imgur.com/aNLHrfE.jpg',
        },
      ],
      inventory: {
        id: id(),
        quantity: 19,
      },
      price: '17800',
      fulfillments: [
        FulfillmentService.SevenEleven,
        FulfillmentService.Ecan,
        FulfillmentService.InStorePickup,
      ],
    },
  ],
},

{
  id: id(),
  name: 'Quick Open易開式不鏽鋼真空保溫杯',
  featureBullets: [
    '新式快開杯蓋，輕鬆開啟，喀嚓一聲即可鎖住',
    '鮮麗亮眼的豐富花色，點綴生活更繽紛',
    '防燙杯口直接飲用，好安心',
    '冷熱兩用，可直接泡茶&咖啡',
    '內面防沾塗層加工，不易沾污、不易發臭',
  ],
  brand: '象印',
  priceCurrency: 'TWD',
  options: [
    {
      id: id(),
      name: '顏色',
    },
    {
      id: id(),
      name: '容量',
    },
  ],
  variants: [
    {
      id: id(),
      options: ['珍珠綠', '0.55L'],
      images: [
        {
          id: id(),
          tag: ImageTag.MainImage,
          handle: 'https://images-na.ssl-images-amazon.com/images/I/61K12vE32QL._SL1500_.jpg',
        },
        {
          id: id(),
          tag: ImageTag.ProductAlt1Image,
          handle: 'https://images-na.ssl-images-amazon.com/images/I/71lD%2B-h6JDL._SL1500_.jpg',
        },
        {
          id: id(),
          tag: ImageTag.ProductAlt2Image,
          handle: 'https://images-na.ssl-images-amazon.com/images/I/61rbI9VMRwL._SL1500_.jpg',
        },
        {
          id: id(),
          tag: ImageTag.ProductAlt3Image,
          handle: 'https://images-na.ssl-images-amazon.com/images/I/21Rl1FcBSVL.jpg',
        },
        {
          id: id(),
          tag: ImageTag.ProductAlt4Image,
          handle: 'https://images-na.ssl-images-amazon.com/images/I/61b0LenuJ8L._SL1500_.jpg',
        },
      ],
      inventory: {
        id: id(),
        quantity: 10,
      },
      sku: 'SM-XS55-GG',
      price: '756',
      fulfillments: [
        FulfillmentService.SevenEleven,
        FulfillmentService.Ecan,
        FulfillmentService.InStorePickup,
      ],
    },
    {
      id: id(),
      options: ['紫紅', '0.55L'],
      images: [
        {
          id: id(),
          tag: ImageTag.MainImage,
          handle: 'https://images-na.ssl-images-amazon.com/images/I/611uFoPyRBL._SL1500_.jpg',
        },
        {
          id: id(),
          tag: ImageTag.ProductAlt1Image,
          handle: 'https://images-na.ssl-images-amazon.com/images/I/71trMmWfpwL._SL1500_.jpg',
        },
        {
          id: id(),
          tag: ImageTag.ProductAlt2Image,
          handle: 'https://images-na.ssl-images-amazon.com/images/I/61lRrY1iDML._SL1500_.jpg',
        },
        {
          id: id(),
          tag: ImageTag.ProductAlt3Image,
          handle: 'https://images-na.ssl-images-amazon.com/images/I/31LKX8tsHDL.jpg',
        },
        {
          id: id(),
          tag: ImageTag.ProductAlt4Image,
          handle: 'https://images-na.ssl-images-amazon.com/images/I/61U97YFL-8L._SL1500_.jpg',
        },
      ],
      inventory: {
        id: id(),
        quantity: 10,
      },
      sku: 'SM-XS55-VK',
      price: '1050',
      fulfillments: [
        FulfillmentService.SevenEleven,
        FulfillmentService.Ecan,
        FulfillmentService.InStorePickup,
      ],
    },

    {
      id: id(),
      options: ['黑', '0.36L'],
      images: [
        {
          id: id(),
          tag: ImageTag.MainImage,
          handle: 'https://images-na.ssl-images-amazon.com/images/I/614X4U5cIkL._SL1500_.jpg',
        },
        {
          id: id(),
          tag: ImageTag.ProductAlt1Image,
          handle: 'https://images-na.ssl-images-amazon.com/images/I/71T61RWwaTL._SL1500_.jpg',
        },
        {
          id: id(),
          tag: ImageTag.ProductAlt2Image,
          handle: 'https://images-na.ssl-images-amazon.com/images/I/61AYptIGOFL._SL1500_.jpg',
        },
        {
          id: id(),
          tag: ImageTag.ProductAlt3Image,
          handle: 'https://images-na.ssl-images-amazon.com/images/I/61TlvQiK7HL._SL1500_.jpg',
        },
        {
          id: id(),
          tag: ImageTag.ProductAlt4Image,
          handle: 'https://images-na.ssl-images-amazon.com/images/I/614X4U5cIkL._SL1500_.jpg',
        },
      ],
      inventory: {
        id: id(),
        quantity: 1,
      },
      sku: 'SM-XA36-DB',
      price: '650',
      fulfillments: [
        FulfillmentService.SevenEleven,
        FulfillmentService.Ecan,
        FulfillmentService.InStorePickup,
      ],
    },

    {
      id: id(),
      options: ['粉紅', '0.36L'],
      images: [
        {
          id: id(),
          tag: ImageTag.MainImage,
          handle: 'https://images-na.ssl-images-amazon.com/images/I/61oXoIA9CwL._SL1500_.jpg',
        },
        {
          id: id(),
          tag: ImageTag.ProductAlt1Image,
          handle: 'https://images-na.ssl-images-amazon.com/images/I/71rfFdRXnUL._SL1500_.jpg',
        },
        {
          id: id(),
          tag: ImageTag.ProductAlt2Image,
          handle: 'https://images-na.ssl-images-amazon.com/images/I/61M-oOUEwLL._SL1500_.jpg',
        },
        {
          id: id(),
          tag: ImageTag.ProductAlt3Image,
          handle: 'https://images-na.ssl-images-amazon.com/images/I/61Wlz86-hwL._SL1500_.jpg',
        },
        {
          id: id(),
          tag: ImageTag.ProductAlt4Image,
          handle: 'https://images-na.ssl-images-amazon.com/images/I/61prcRC-52L._SL1500_.jpg',
        },
      ],
      inventory: {
        id: id(),
        quantity: 5,
      },
      sku: 'SM-XA36-PA',
      price: '650',
      fulfillments: [
        FulfillmentService.SevenEleven,
        FulfillmentService.Ecan,
        FulfillmentService.InStorePickup,
      ],
    },
  ],
},
];

export default products;
